import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SinConexionKawaii from "../SinConexionKawaii";

// Template171: Día 1 Mapa Downtown SF - lee todo desde page
const Template171 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const { setMapFullscreen } = useMapFullscreen() || {};

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page.texts?.[0]?.content ?? "";
	const footerText = page.texts?.[1]?.content ?? "";
	const mapUrl = page.maps?.[0] ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const offlineMapSrc = getImageSrc("puerto-sf");
	const tipBox =
		page?.tipBox ?? page?.data?.tipBox ?? page?.data?.data?.tipBox ?? {};
	const tipBoxText = tipBox.text ?? "";
	const tipBoxImageId = tipBox.imageId ?? "atardecer-puerto";
	const tipBoxImageSrc = getImageSrc(tipBoxImageId);
	const actualHeaderColor = headerBackgroundColor || "#F2B705";
	const contentColor = textColor || "#444";

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: "#fff!important",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: "'Poppins', sans-serif",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					zIndex: 2,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}
				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}
				{isOnline && mapUrl ? (
					<iframe
						title="Mapa Downtown SF"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					/>
				) : offlineMapSrc ? (
					<Box
						component="img"
						src={offlineMapSrc}
						alt=""
						sx={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
							position: "relative",
						}}
					/>
				) : !isOnline ? (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				) : null}
				{tipBoxText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 60,
							right: 5,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: "'Poppins', sans-serif",
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
							zIndex: 3,
						}}
					>
						{tipBoxText}
					</Box>
				)}
				{tipBoxImageSrc && (
					<Box
						component="img"
						src={tipBoxImageSrc}
						alt="Atardecer"
						sx={{
							width: 110,
							position: "absolute",
							bottom: -20,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}
			</Box>
			{footerText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontFamily: "'Poppins', sans-serif",
						fontSize: 12,
						color: contentColor,
					}}
				>
					{footerText}
				</Box>
			)}
		</Box>
	);
};

Template171.thumbnail = "/images/template-1.png";
export default Template171;
